package horcrux.frontend.common.views.org

import horcrux.frontend.common.configs.StaticContentConfigs.CHROME_EXTENSION_URL
import horcrux.frontend.common.withDefaultCSS
import js.objects.jso
import mui.material.Container
import mui.material.Link
import mui.material.Typography
import mui.material.TypographyProps
import mui.material.styles.TypographyVariant
import mui.system.sx
import react.FC
import react.Props
import react.dom.html.ReactHTML.br
import web.cssom.WhiteSpace
import kotlin.js.Date

external interface LatestNewsProps : Props

val LatestNews = FC<LatestNewsProps> {
    Container {
        this.withDefaultCSS()

        ReleaseNote {
            this.releaseDate = Date(2025, 0, 25)
            this.typographyProps = jso {
                +"""
                • Made UI for exploring documents and document chunks more user friendly 
                """.trimIndent()
            }
        }

        ReleaseNote {
            this.releaseDate = Date(2024, 10, 26)
            this.typographyProps = jso {
                +"""
                • Added support for importing multiple documents at once
                • Added support for automatic detection of document type for uploaded files
                • Added better feedback for errors found when importing documents 
                """.trimIndent()
            }
        }

        ReleaseNote {
            this.releaseDate = Date(2024, 10, 3)
            this.typographyProps = jso {
                +"""
                • Added support for importing multiple documents at once
                • Add support for gpt-4o models
                """.trimIndent()
            }
        }

        ReleaseNote {
            this.releaseDate = Date(2024, 8, 7)
            this.typographyProps = jso {
                +"""
                • Added search functionality to collection selector
                • Removed import documents button from chat pages, since it is not relevant to chat page functionality
                • Introduced breaking changes to Chrome extension. Please upgrade to version 0.9.0.
                """.trimIndent()
            }
        }

        ReleaseNote {
            this.releaseDate = Date(2024, 7, 3)
            this.typographyProps = jso {
                +"""
                • Made chat page URLs more user friendly. They can now be accessed through thegrokapp.com domain name. 
                """.trimIndent()
            }
        }

        ReleaseNote {
            this.releaseDate = Date(2024, 6, 23)
            this.typographyProps = jso {
                +"""
                • Fix bug where documents in public chat page are not viewable by other users
                • Fix bug where PDFs are not uploaded properly 
                """.trimIndent()
            }
        }

        ReleaseNote {
            this.releaseDate = Date(2024, 6, 9)
            this.typographyProps = jso {
                +"""
                • Release Chrome Extension v0.6
                • Add create chat page functionality to Chrome Extension
                • Fix bug where Chrome Extension is unable to load collections
                """.trimIndent()
            }
        }

        ReleaseNote {
            this.releaseDate = Date(2024, 6, 7)
            this.typographyProps = jso {
                +"""
                • Launched Publish chat feature that enables users to create public Chat Pages from their private collections that are sharable via URL.
                • Launched import public Chat Page feature that enables users to import a public Chat Page to a private Collection.
                """.trimIndent()
            }
        }

        ReleaseNote {
            this.releaseDate = Date(2024, 5, 15)
            this.typographyProps = jso {
                +"""
                • Integrated login screen into app.
                """.trimIndent()
            }
        }

        ReleaseNote {
            this.releaseDate = Date(2024, 4, 10)
            this.typographyProps = jso {
                +"""
                • Launched UI redesign with chat interface for sending messages to The Grok App.
                """.trimIndent()
            }
        }

        ReleaseNote {
            this.releaseDate = Date(2024, 3, 27)
            this.typographyProps = jso {
                +"""
                • Improved landing page for greater accessibility and SEO.
                """.trimIndent()
            }
        }

        ReleaseNote {
            this.releaseDate = Date(2024, 3, 22)
            this.typographyProps = jso {
                +"""
                • Fixed bug in Chrome Extension where context menu buttons would become non functional after 30 seconds of inactivity
                • Added Chrome Extension demo video
                """.trimIndent()
            }
        }

        ReleaseNote {
            this.releaseDate = Date(2024, 3, 16)
            this.typographyProps = jso {
                +"""
                • Added support for OpenAI gpt-4-turbo chat model
                • Upgraded embedding model to OpenAI text-embedding-3-small
                • Fixed bug causing delete document functionality to fail
                """.trimIndent()
            }
        }

        ReleaseNote {
            this.releaseDate = Date(2024, 3, 16)
            this.typographyProps = jso {
                +"""
                • Released
                """.trimIndent()
                Link {
                    +" Chrome Extension"
                    this.href = CHROME_EXTENSION_URL
                }
                br {}
                +"""
                • The extension allows users to ask questions about their documents in the Chrome side panel and supports importing documents from a text selection and the full file.
                """.trimIndent()
            }
        }

        ReleaseNote {
            this.releaseDate = Date(2023, 10, 6)
            this.typographyProps = jso {
                +"""
                • Introduced support for uploading files from browser
                • Introduced support for importing full documents and viewing them from browser
                """.trimIndent()
            }
        }

        ReleaseNote {
            this.releaseDate = Date(2023, 8, 22)
            this.typographyProps = jso {
                +"""
                • Introduced support for PDF document types
                """.trimIndent()
            }
        }

        ReleaseNote {
            this.releaseDate = Date(2023, 8, 15)
            this.typographyProps = jso {
                +"""
                • Made UI improvements. Added "Ask Question" and "Manage Documents" buttons for easier navigation.
                • Added demo to landing page
                """.trimIndent()
            }
        }

        ReleaseNote {
            this.releaseDate = Date(2023, 8, 7)
            this.typographyProps = jso {
                +"""
                • Support for HTML documents
                • Added feedback form
                """.trimIndent()
            }
        }

        ReleaseNote {
            this.releaseDate = Date(2023, 8, 1)
            this.typographyProps = jso {
                +"""
                • Initial release
                • Support for importing text documents from urls into collections
                • Support for managing collections
                • Support for asking questions about collections using chat models
                """.trimIndent()
            }
        }
    }
}

external interface ReleaseNoteProps : Props {
    var releaseDate: Date
    var typographyProps: TypographyProps
}

val ReleaseNote = FC<ReleaseNoteProps> { props ->
    Typography {
        +"Release on ${props.releaseDate.toDateString()}"
        this.variant = TypographyVariant.h6
    }
    props.typographyProps.paragraph = true
    props.typographyProps.sx {
        this.whiteSpace = WhiteSpace.preLine
    }
    Typography {
        +props.typographyProps
    }
}
